import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoClose } from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { getComplaintsByRealtor } from '../../../services/complaintService';
import Spinner from '../../../components/common/Spinner';

const ComplaintList = () => {
  const { userData } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userData?.uid) return;
    setLoading(true);
    getComplaintsByRealtor(userData.uid)
      .then(setComplaints)
      .catch(() => setComplaints([]))
      .finally(() => setLoading(false));
  }, [userData?.uid]);

  return (
    <Container>
      <Title>민원 관리</Title>
      <Desc>저평점·불만 건을 확인하세요</Desc>

      {loading ? (
        <Spinner fullPage={false} />
      ) : complaints.length > 0 ? (
        <List>
          {complaints.map((item) => (
            <Card key={item.id} onClick={() => setSelected(item)}>
              <BadgeRow>
                <Badge $type="danger">{item.type || '민원'}</Badge>
                <Badge $type="outline">{item.status === 'pending' ? '대기' : item.status === 'resolved' ? '완료' : item.status}</Badge>
              </BadgeRow>
              <InfoRow>
                <span>{item.customerName || '—'}</span>
                <Arrow>→</Arrow>
                <span>{item.partnerName || '—'}</span>
              </InfoRow>
              <Content>{item.content || item.summary || '—'}</Content>
              <DateText>{item.date}</DateText>
            </Card>
          ))}
        </List>
      ) : null}

      {!loading && complaints.length === 0 && (
        <EmptyBox>
          <EmptyIcon>!</EmptyIcon>
          <EmptyTitle>민원이 없습니다</EmptyTitle>
          <EmptyDesc>접수된 민원이 없습니다.</EmptyDesc>
        </EmptyBox>
      )}

      {/* 바텀시트 */}
      {selected && (
        <Overlay onClick={() => setSelected(null)}>
          <BottomSheet onClick={(e) => e.stopPropagation()}>
            <SheetHeader>
              <SheetTitle>민원 상세</SheetTitle>
              <CloseBtn onClick={() => setSelected(null)}>
                <IoClose size={22} />
              </CloseBtn>
            </SheetHeader>
            <SheetBody>
              <DetailBadgeRow>
                <Badge $type="danger">{selected.type}</Badge>
                <Badge $type="outline">{selected.status}</Badge>
              </DetailBadgeRow>
              <DetailRow>
                <DetailLabel>고객 / 업체</DetailLabel>
                <DetailValue>{selected.customer}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>민원 내용</DetailLabel>
                <DetailValue>{selected.content}</DetailValue>
              </DetailRow>
              <DetailRow>
                <DetailLabel>등록일시</DetailLabel>
                <DetailValue>{selected.date}</DetailValue>
              </DetailRow>
            </SheetBody>
          </BottomSheet>
        </Overlay>
      )}
    </Container>
  );
};

export default ComplaintList;

// ───── 스타일 ─────
const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const Desc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing.xs} 0 ${({ theme }) => theme.spacing.lg};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base};
  cursor: pointer;
`;

const BadgeRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  border-radius: ${({ theme }) => theme.radius.sm};
  white-space: nowrap;

  ${({ $type, theme }) =>
    $type === 'danger'
      ? `background: ${theme.colors.danger}; color: #fff;`
      : `background: ${theme.colors.bgWhite}; color: ${theme.colors.textSecondary}; border: 1px solid ${theme.colors.border};`}
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Arrow = styled.span`
  color: ${({ theme }) => theme.colors.textLight};
`;

const Content = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: ${({ theme }) => theme.spacing.xs} 0;
`;

const DateText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xxl} 0;
`;

const EmptyIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ theme }) => theme.colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.xl};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const EmptyTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const EmptyDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// ───── 바텀시트 ─────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const BottomSheet = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.lg} ${({ theme }) => theme.radius.lg} 0 0;
  width: 100%;
  max-width: ${({ theme }) => theme.realtor.contentMaxWidth};
  max-height: 70vh;
  overflow-y: auto;
`;

const SheetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.realtor.pagePadding};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const SheetTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
`;

const SheetBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.realtor.pagePadding};
`;

const DetailBadgeRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const DetailRow = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const DetailLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const DetailValue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;
